import { useLocalStorage } from "@uidotdev/usehooks";
import useListAlertsSummary from "api/queries/useListAlertsSummary";
import { Tab } from "autoGenSubTypes";
import { useMemo } from "react";
import {
	defaultTimeFilterOption,
	timeFilterOptions,
} from "routes/alerts/:tabKey/TimeFilterOptions";
import { timeFilterAfterParam } from "utils/alertUtils";
import useLastActiveTime from "./useLastActiveTime";

/**
 * A custom hook for getting a count of how many alerts have come in since you were last on the tab.
 *
 * @param tab the related tab data for this tab
 * @returns the number of alerts that came in since the tab was last active destructured as { missedAlertsCount }
 */
const useMissedAlertsCount = (tab: Tab) => {
	const { lastActiveTime } = useLastActiveTime(tab);

	const alertsTabData = tab?.__typename === "AlertsTab" && tab?.alerts_tab_data;

	/** query for alerts in top pane */
	const streamIdsTop =
		alertsTabData ?
			alertsTabData.top.map((tab) => tab.stream_id).filter(Boolean)
		:	[];

	// Read time filter from localStorage for this tab
	const topStorageKey = `alerts.top.timeFilter.${tab.key}`;
	const [topSelectedValue] = useLocalStorage(
		topStorageKey,
		defaultTimeFilterOption.value
	);

	// Get the hours value from the selected time filter option
	const topTimeFilterHours = useMemo(() => {
		const option =
			timeFilterOptions.find((opt) => opt.value === topSelectedValue) ||
			defaultTimeFilterOption;
		return option.hours;
	}, [topSelectedValue]);

	const topTimeFilterParam = useMemo(
		() => timeFilterAfterParam(topTimeFilterHours),
		[topTimeFilterHours]
	);

	const { data: alertsTop } = useListAlertsSummary({
		backfill: true,
		after: topTimeFilterParam,
		count: 100,
		stream_ids: streamIdsTop,
	});

	/** query for alerts in bottom pane */
	const streamIdsBottom =
		alertsTabData ?
			alertsTabData.bottom.map((tab) => tab.stream_id).filter(Boolean)
		:	[];

	// Read time filter from localStorage for this tab
	const bottomStorageKey = `alerts.bottom.timeFilter.${tab.key}`;
	const [bottomSelectedValue] = useLocalStorage(
		bottomStorageKey,
		defaultTimeFilterOption.value
	);

	// Get the hours value from the selected time filter option
	const bottomTimeFilterHours = useMemo(() => {
		const option =
			timeFilterOptions.find((opt) => opt.value === bottomSelectedValue) ||
			defaultTimeFilterOption;
		return option.hours;
	}, [bottomSelectedValue]);

	const bottomTimeFilterParam = useMemo(
		() => timeFilterAfterParam(bottomTimeFilterHours),
		[bottomTimeFilterHours]
	);

	const { data: alertsBottom } = useListAlertsSummary({
		backfill: true,
		after: bottomTimeFilterParam,
		count: 100,
		stream_ids: streamIdsBottom,
	});

	const countMissedAlerts = (alerts: typeof alertsTop) =>
		(alerts ?? []).filter(
			(alert) => new Date(alert.created).getTime() > lastActiveTime
		).length;

	const topMissedAlerts = countMissedAlerts(alertsTop);
	const bottomMissedAlerts = countMissedAlerts(alertsBottom);

	return {
		bottomMissedAlerts,
		topMissedAlerts,
		totalMissedAlerts: topMissedAlerts + bottomMissedAlerts,
	};
};

export default useMissedAlertsCount;
